'use client';

import {
  createContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';
import { authService } from '@/services/api/auth';
import { setLogoutCallback } from '@/lib/utils/api';
import { analytics } from '@/lib/monitoring/analytics';
import { CricketDetails, FootballDetails } from '../lib/types/enrollment';

interface User {
  id: string;
  email: string;
  name: string | null;
  profilePicture?: string | null;
  role: 'admin' | 'guest' | 'default';
  sportDetails?: {
    [key: string]: CricketDetails | FootballDetails;
  };
  dateOfBirth?: string;
  gender?: 'male' | 'female' | 'other' | 'prefer-not-to-say';
  countryCode?: string;
  mobileNumber?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  loginWithGoogle: (code: string) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

interface AuthProviderProps {
  children: ReactNode;
}

/** 🔹 Helper function to extract error message */
const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'An unexpected error occurred';

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const router = useRouter();

  // Prevent hydration mismatch by ensuring client-side rendering
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  /** 🔹 Initialize Auth State */
  useEffect(() => {
    if (!isHydrated) return; // Wait for hydration

    const initializeAuth = async () => {
      setIsAuthChecking(true);
      const storedToken = localStorage.getItem('token');

      if (!storedToken) {
        setIsInitialized(true);
        setIsAuthChecking(false);
        return;
      }

      // Set token immediately - assume it's valid until proven otherwise
      setToken(storedToken);

      try {
        const userProfile = await authService.getProfile();
        setUser(userProfile);
      } catch (error) {
        const errorMessage = getErrorMessage(error);

        // Only clear auth state for actual authentication failures
        if (
          errorMessage.includes('Session expired') ||
          errorMessage.includes('401') ||
          errorMessage.includes('Unauthorized')
        ) {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
        // For network errors, keep token and let user retry
      } finally {
        setIsInitialized(true);
        setIsAuthChecking(false);
      }
    };

    initializeAuth();
  }, [isHydrated]);

  /** 🔹 User Login */
  const login = async (credentials: { email: string; password: string }) => {
    try {
      const { token: newToken } = await authService.login(credentials);
      localStorage.setItem('token', newToken);
      setToken(newToken);

      const userProfile = await authService.getProfile();
      setUser(userProfile);

      // Set user context in analytics immediately after login
      analytics.setUserContext({
        userId: userProfile.id,
        userEmail: userProfile.email,
        userName: userProfile.name || undefined,
        userRole: userProfile.role,
      });

      // Track successful login
      analytics.trackUserAction('login_success', {
        userId: userProfile.id,
        userRole: userProfile.role,
        method: 'email',
      });
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.error('❌ Login Failed:', errorMessage);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);

      // Track failed login
      analytics.trackUserAction('login_failed', {
        error: errorMessage,
        method: 'email',
      });

      throw new Error(errorMessage);
    }
  };

  /** 🔹 Google Login */
  const loginWithGoogle = async (code: string) => {
    try {
      const { token: newToken } = await authService.loginWithGoogle({
        code,
      });
      localStorage.setItem('token', newToken);
      setToken(newToken);

      const userProfile = await authService.getProfile();
      setUser(userProfile);

      // Set user context in analytics immediately after login
      analytics.setUserContext({
        userId: userProfile.id,
        userEmail: userProfile.email,
        userName: userProfile.name || undefined,
        userRole: userProfile.role,
      });

      // Track successful login
      analytics.trackUserAction('login_success', {
        userId: userProfile.id,
        userRole: userProfile.role,
        method: 'google',
      });
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.error('❌ Google Login Failed:', errorMessage);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);

      // Track failed login
      analytics.trackUserAction('login_failed', {
        error: errorMessage,
        method: 'google',
      });

      throw new Error(errorMessage);
    }
  };

  /** 🔹 User Logout */
  const logout = useCallback(async () => {
    if (user) {
      try {
        await authService.logout();
      } catch (error) {
        console.error('Logout error:', error);
      }
    }

    // Clear auth state immediately
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');

    // Only navigate to login if not on a public page that can be viewed without auth
    const currentPath = window.location.pathname;
    const publicPages = ['/tournament/', '/team/', '/player/', '/sports/'];
    const isOnPublicPage = publicPages.some((page) =>
      currentPath.includes(page)
    );

    if (!isOnPublicPage) {
      router.push('/auth/login');
    }
  }, [router, user]);

  /** 🔹 Register logout callback */
  useEffect(() => {
    setLogoutCallback(logout);
    return () => setLogoutCallback(() => {});
  }, [logout]);

  /** 🔹 Update User Profile */
  const updateUser = (updatedUser: User) => setUser(updatedUser);

  // Always render children - don't return null during hydration
  return (
    <AuthContext.Provider
      value={{
        user: isHydrated && isInitialized ? user : null,
        token: isHydrated && isInitialized ? token : null,
        isAuthenticated:
          isHydrated && isInitialized && !isAuthChecking
            ? Boolean(token && user)
            : false,
        isInitialized: isHydrated && isInitialized && !isAuthChecking,
        login,
        loginWithGoogle,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
